<template>
  <template v-if="loadingError">
    <div class="fs-5 text-primary d-inline-flex">
      {{ loadingError }}
    </div>
  </template>
  <template v-if="identity">
    <HeaderSection :identity="identity" />
    <div class="vff" :class="{ 'vff--mobile': isMobile }">
      <div class="f-container">
        <div class="vff-animate q-form f-fade-in-up">
          <Breadcrumb
            v-if="breadcrumb?.length > 0"
            class="mb-2 container-x-padding"
            :items="breadcrumb"
          />
          <slot v-if="hasNamedSlot('title')" name="title" />
          <TitleSection
            v-else
            :title="title"
            @previousStepClick="$emit('previousStepClick')"
            :previous-step-route="previousStepRoute"
          />

          <slot />
        </div>
      </div>
    </div>

    <FooterSection :identity="identity" />
    <Transition name="slide-in slide-out">
      <ConsentQuestion
        v-if="showCookieConsentCompliance"
        :show-privacy-policy-link="shouldShowPrivacyPolicy"
        @cookie-compliance="hasCookieConsent = true"
      />
    </Transition>
  </template>
</template>
<script lang="ts">
import { defineComponent, onBeforeMount, computed, ref } from 'vue'
import HeaderSection from '~concierge/components/HeaderSection.vue'
import FooterSection from '~concierge/components/FooterSection.vue'
import TitleSection from '~concierge/components/TitleSection.vue'
import ConsentQuestion from '~conciergeUiCommons/components/ConsentQuestion.vue'
import { IdentityService } from '~concierge/services/IdentityService'
import { IdentityData } from '~concierge/types/IdentityTypes'
import { useIdentityStore } from '~conciergeUiCommons/store/identityStore'
import { usePage } from '@inertiajs/vue3'
import { useMobileDetection } from '~conciergeUiCommons/composables/deviceDetection'
import Breadcrumb from '~concierge/components/Breadcrumb.vue'
import { BreadcrumbResource } from '~types/concierge'
import { trans } from 'laravel-vue-i18n'

export default defineComponent({
  components: { HeaderSection, FooterSection, TitleSection, ConsentQuestion, Breadcrumb },
  props: {
    title: {
      type: String,
      required: true
    },
    previousStepRoute: {
      type: String,
      default: ''
    },
    longContent: {
      type: Boolean,
      default: false
    },
    breadcrumb: {
      type: Array as () => { [key: number]: BreadcrumbResource },
      required: false
    }
  },
  setup(props, { slots }) {
    const hasNamedSlot = (name: string): boolean => {
      return !!slots[name]
    }
    const page = usePage()
    const { isIos, isMobile } = useMobileDetection()
    const hasCookieConsent = ref<boolean>(false)
    const requiredCookieConsent = computed(
      (): boolean => page.props.requiredCookieConsent as boolean
    )

    const showCookieConsentCompliance = computed((): boolean => {
      return requiredCookieConsent.value && !hasCookieConsent.value
    })

    const identity = ref<IdentityData | null>(null)
    const identityStore = useIdentityStore()
    const loadingError = ref<string | null>(null)

    onBeforeMount(async () => {
      try {
        const appStatus = await IdentityService.getAppStatus()

        if (appStatus.redirectToMaintenancePage) {
          return (window.location.href = '/alfred-form-maintenance')
        }
      } catch (error) {
        console.error('Unable to fetch concierge app status', error)
        loadingError.value = trans('messages.loading_error')
        return
      }

      try {
        const identityData = page.props.identity as IdentityData
        identityStore.setIdentity(identityData)
        identity.value = identityStore.identity
      } catch (error) {
        console.error('Unable to fetch concierge identity setup', error)
        loadingError.value = trans('messages.loading_error')
      }
    })

    const loaderContainer = document.getElementById('loaderContainer')
    if (loaderContainer) {
      loaderContainer.remove()
      window.clearLoaderTimeout?.()
    }

    return {
      showCookieConsentCompliance,
      hasNamedSlot,
      isIos,
      isMobile,
      identity,
      loadingError,
      hasCookieConsent
    }
  },
  watch: {
    identity: {
      immediate: true,
      handler() {
        if (this.identity) {
          // if (this.longContent) {
          document.body.className = `${this.bodyBaseClass} ${this.bodyBaseClass}--long-content`
          // } else {
          //     document.body.className = this.bodyBaseClass
          // }
        }
      }
    },

    longContent: {
      immediate: true,
      handler() {
        // if (this.longContent) {
        document.body.className = `${this.bodyBaseClass} ${this.bodyBaseClass}--long-content`
        // } else {
        //     document.body.className = this.bodyBaseClass
        // }
      }
    }
  },
  emits: ['previousStepClick'],
  computed: {
    isDesktop() {
      return !this.isIos && !this.isMobile
    },
    bodyBaseClass() {
      if (this.isDesktop) {
        return 'desktop'
      } else {
        return 'mobile'
      }
    },
    shouldShowPrivacyPolicy(): boolean {
      return this.identity?.contact?.province === 'QC'
    }
  }
})
</script>
